import { useState } from 'react';
import { ShieldCheck, CircleNotch } from '@phosphor-icons/react';

import { changePassword } from '@/lib/meApi';
import { getApiErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/features/auth/store';
import { useLogout } from '@/features/auth/hooks';
import { PasswordChangeFields } from './PasswordChangeFields';
import { emptyPasswordForm, isPasswordFormReady } from '@/lib/passwordPolicy';
import { ScrollLock } from '@/components/shared/ScrollLock';

/**
 * Forced first-sign-in password change.
 *
 * A member invited by their chambers admin is given a temporary password out of band — so the
 * admin knows it too. That is fine as a way THROUGH THE DOOR ONCE; it is not a credential anyone
 * should still be using next week. The invite dialog already promises the member "will be required
 * to set their own password on first sign-in"; this is the component that makes that true.
 *
 * Deliberately NOT dismissible: no close button, no escape key, no backdrop click. A member who
 * dismissed it would keep working on a password their admin knows, and nothing would ask again.
 * The only ways out are setting a password, or signing out.
 *
 * The fields, rules and strength meter are the SAME component Settings → Security uses — one
 * implementation, so the two screens can never disagree about what a good password is.
 */
export function ForcePasswordChangeModal() {
  const user = useAuthStore((s) => s.user);
  const patchUser = useAuthStore((s) => s.patchUser);
  const logout = useLogout();

  const [form, setForm] = useState(emptyPasswordForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user?.mustChangePassword) return null;

  const ready = isPasswordFormReady(form);

  async function submit() {
    if (!ready || saving) return;
    setSaving(true);
    setError(null);
    try {
      await changePassword(form.current, form.next);
      // The server has cleared the flag; mirror it locally so the modal closes immediately rather
      // than waiting for the next session refresh.
      patchUser({ mustChangePassword: false });
      setForm(emptyPasswordForm);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="force-password-title"
    >
      <ScrollLock />
      <div className="my-auto w-full max-w-md rounded-xl border border-border bg-card shadow-xl">
        <div className="flex items-center gap-2 border-b border-border px-5 py-4">
          <ShieldCheck size={18} weight="duotone" className="text-brand-600" />
          <h2 id="force-password-title" className="text-sm font-semibold text-foreground">
            Set your own password
          </h2>
        </div>

        <div className="space-y-4 px-5 py-4">
          <p className="text-sm text-muted-foreground">
            You signed in with a temporary password that was given to you by your chambers. Someone
            else knows it, so it cannot stay on your account. Choose a password only you know — you
            will use it from now on.
          </p>

          <PasswordChangeFields
            value={form}
            onChange={setForm}
            currentLabel="Temporary password"
            disabled={saving}
          />

          {error && (
            <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-4">
          {/* The only way out other than finishing. Kept quiet so it does not read as a way to skip. */}
          <button
            type="button"
            onClick={() => logout.mutate()}
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
          >
            Sign out instead
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!ready || saving}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
          >
            {saving && <CircleNotch size={13} className="animate-spin" />}
            {saving ? 'Saving…' : 'Set password'}
          </button>
        </div>
      </div>
    </div>
  );
}
